import {
  ALERT_BROADCAST_CHANNEL_TIMEOUT,
  ALERT_MANAGER_EVENTS,
  INSTANCE_DEFAULT,
} from '../utils/spds/constants/alert-manager';
import { buildWaiter } from '@ember/test-waiters';
import { capitalize } from '@ember/string';
import { registerDestructor } from '@ember/destroyable';
import { waitForNext } from '../utils/waiters';
import Service from '@ember/service';

let waiter = buildWaiter('alert-manager-broadcast');

/**
 * Alert Manager Service
 *
 * Central event bus for managing UI feedback components (modals, toasts, banners).
 * Components register event handlers with this service, and other services broadcast
 * events to trigger UI updates. Prevents race conditions by waiting for handlers to
 * register before broadcasting events.
 *
 * @class AlertManagerService
 * @extends Service
 */
export default class AlertManagerService extends Service {
  events = ALERT_MANAGER_EVENTS;
  global = {};

  /**
   * Generates and throws an error with a formatted message
   * @private
   * @param {Object} params - Error parameters
   * @param {string} [params.event] - The event type
   * @param {string} [params.message] - Custom error message
   * @param {string} [params.type] - The alert type
   * @throws {Error} Always throws an error with the formatted message
   */
  #generateError({ event, message, type }) {
    throw new Error(message || `No ${event} event associated with alert type ${type}`);
  }

  /**
   * Parses and formats an event name from event type and action
   * Example: { event: 'display', type: 'modal' } -> 'displayModal'
   * @private
   * @param {Object} params - Event parameters
   * @param {string} [params.event='display'] - The event action (e.g., 'display', 'clear')
   * @param {string} params.type - The alert type (e.g., 'modal', 'toast', 'banner')
   * @returns {string} The formatted event name in camelCase
   */
  #parseEventName({ event = 'display', type }) {
    return `${event}${type.charAt(0).toUpperCase()}${type.slice(1)}`;
  }

  /**
   * Executes the registered event handler for a specific event
   * @private
   * @param {Object} params - Event execution parameters
   * @param {string} [params.event] - The event action type
   * @param {string} params.eventName - The formatted event name
   * @param {string} [params.instance='global'] - The instance/container ID
   * @param {*} params.message - The message/data to pass to the handler
   * @param {string} params.type - The alert type
   * @returns {*} The return value from the event handler
   * @throws {Error} If no handler is registered for the event
   */
  #runEvent = ({ event, eventName, instance = INSTANCE_DEFAULT, message, type }) => {
    if (this[instance][eventName]) {
      return this[instance][eventName](message);
    }

    this.#generateError({ event, type });
  };

  /**
   * Binds an event handler to a component context with automatic cleanup
   * Creates a bound handler, registers it with the service, and sets up a destructor
   * to automatically unregister the handler when the component is destroyed.
   *
   * @param {Object} params - Binding parameters
   * @param {Object} params.context - The component context to bind to (must have alertManager service)
   * @param {string} params.event - The event name to bind
   * @param {string} [params.instance='global'] - The instance/container ID
   * @param {Function} params.handler - The handler function to bind
   *
   * @example
   * // In a component
   * this.alertManager.bindEventHandler({
   *   context: this,
   *   event: 'displayModal',
   *   handler: this.handleModal
   * });
   */
  bindEventHandler({ context, event, instance = INSTANCE_DEFAULT, handler }) {
    let handlerName = `_bound${capitalize(event)}Handler`;

    context[handlerName] = handler.bind(context);

    this.on(event, context[handlerName], instance);

    registerDestructor(context, () => {
      context.alertManager.off(event, instance);
      delete context[handlerName];
    });
  }

  /**
   * Broadcasts an event to trigger UI feedback (modal, toast, or banner)
   * Waits for the handler to be registered before broadcasting to prevent race conditions.
   * This method is synchronous in execution (returns immediately) but uses async/await
   * for test waiter integration.
   *
   * @param {string} type - The event type constant (e.g., ALERT_MANAGER_EVENT_TYPE_MODAL)
   * @param {Object} message - The data/configuration to pass to the handler
   * @param {string} [message.containerId] - Optional container ID for local instances
   * @returns {Promise<*>} The return value from the event handler (e.g., modal ID)
   * @throws {Error} If the event handler is not registered or times out
   *
   * @example
   * await this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_TOAST, {
   *   message: 'Success!',
   *   type: 'success'
   * });
   */
  async broadcast(type, message) {
    let token = waiter.beginAsync();

    try {
      let eventName = this.#parseEventName({ event: message?.event, type });
      let instance = message?.containerId || INSTANCE_DEFAULT;

      await this.waitForBroadcastChannel(eventName, instance);

      return this.#runEvent({ eventName, instance, message, type });
    } finally {
      waiter.endAsync(token);
    }
  }

  /**
   * Clears/closes UI feedback components (modals, toasts, or banners)
   * This method executes asynchronously to allow for cleanup operations.
   *
   * @param {string} type - The event type (e.g., 'modal', 'toast', 'banner')
   * @param {string} [instance='global'] - The instance/container ID
   * @param {*} [message] - Optional data to pass to the clear handler (e.g., modal ID)
   * @returns {Promise<*>} The return value from the clear handler (e.g., true if closed)
   * @throws {Error} If the clear handler is not registered
   *
   * @example
   * // Clear a specific modal
   * await this.alertManager.clear('modal', 'global', modalId);
   *
   * @example
   * // Clear all banners in a container
   * await this.alertManager.clear('banner', 'my-container');
   */
  async clear(type, instance = INSTANCE_DEFAULT, message) {
    let event = 'clear';
    let eventName = this.#parseEventName({ event, type });

    return await this.#runEvent({ event, eventName, instance, message, type });
  }

  /**
   * Checks if an event name is valid/supported
   * @param {string} eventName - The event name to validate
   * @returns {boolean} True if the event is supported, false otherwise
   */
  isValidEvent(eventName) {
    return this.events.includes(eventName);
  }

  /**
   * Unregisters an event handler
   * @param {string} eventName - The event name to unregister
   * @param {string} [instance='global'] - The instance/container ID
   *
   * @example
   * this.alertManager.off('displayModal');
   */
  off(eventName, instance = INSTANCE_DEFAULT) {
    if (this[instance]) {
      delete this[instance][eventName];
    }
  }

  /**
   * Registers an event handler for a specific event
   * @param {string} eventName - The event name to register
   * @param {Function} handler - The handler function to call when event is broadcast
   * @param {string} [instance='global'] - The instance/container ID
   * @throws {Error} If the event name is not valid
   *
   * @example
   * this.alertManager.on('displayModal', (data) => {
   *   // Handle modal display
   *   return modalId;
   * });
   */
  on(eventName, handler, instance = INSTANCE_DEFAULT) {
    if (this.isValidEvent(eventName)) {
      this[instance][eventName] = handler;

      return;
    }

    this.#generateError({ message: `Undefined event ${eventName}` });
  }

  /**
   * Registers a component instance as an event handler container
   * Creates an instance namespace for handlers and sets up automatic cleanup.
   *
   * @param {Object} instance - The component instance
   * @param {Object} instance.args - Component arguments
   * @param {string} instance.args.id - The container ID to use for this instance
   *
   * @example
   * // In a component constructor
   * this.alertManager.registerComponent(this);
   */
  registerComponent(instance) {
    let { id } = instance.args;

    this[id] = {};

    registerDestructor(instance, () => {
      delete this[id];
    });
  }

  /**
   * Waits for an event handler to be registered before allowing broadcast
   * This prevents race conditions when broadcasting may occur before a handler is registered
   * (e.g., on page load). Polls until the handler exists or timeout is reached.
   *
   * @param {string} eventName - The event name to wait for
   * @param {string} [instance='global'] - The instance/container ID
   * @returns {Promise<void>} Resolves when handler is registered
   * @throws {Error} If the event is invalid or handler doesn't register within timeout (1000ms)
   *
   * @example
   * await this.waitForBroadcastChannel('displayModal');
   */
  async waitForBroadcastChannel(eventName, instance = INSTANCE_DEFAULT) {
    if (this.isValidEvent(eventName)) {
      let count = 0;
      let waiting = !this[instance]?.[eventName];

      while (waiting && count < ALERT_BROADCAST_CHANNEL_TIMEOUT) {
        await waitForNext();

        waiting = !this[instance]?.[eventName];
        count++;
      }

      if (waiting) {
        this.#generateError({
          message: `Attempting to wait for broadcast channel for event "${eventName}" but it did not resolve in time`,
        });
      }

      return;
    }

    this.#generateError({
      message: `Attempting to wait for broadcast channel for event "${eventName}" but it will never resolve!`,
    });
  }
}
